import { Check, CheckCheck, Clock, Loader2 } from "lucide-react";
import type { StatusEnvioProposta } from "@/lib/leadSmsApi";

const PAPEL: Record<string, string> = { primaria: "primária", secundaria: "secundária", reserva: "reserva da equipe" };

function hora(iso: string) {
  return new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
}

export function EnvioPropostaStatus({ status, enviando }: { status: StatusEnvioProposta | undefined; enviando: boolean }) {
  if (!status) return null;
  const { linhas, sai_por, ultimo, bloqueio } = status;
  const usadaNoCadastro = linhas.some((l) => l.usada);

  return (
    <div className="space-y-2">
      <div>
        <p className="mb-1 text-[9px] font-bold uppercase tracking-[1.1px] text-muted-foreground">{enviando ? "Saindo por" : "Vai sair por"}</p>
        <div className="space-y-1 rounded-lg border border-border bg-muted px-2.5 py-2">
          {linhas.map((l) => {
            const pula = !l.no_ar && !l.usada;
            return (
              <div key={l.papel} className={`flex min-w-0 items-center gap-2 text-[11.5px] ${pula ? "text-muted-foreground" : ""}`}>
                <span
                  aria-hidden
                  className={`h-2 w-2 flex-none rounded-full ${l.usada ? "bg-[#128C7E] shadow-[0_0_0_3px_rgba(7,94,84,0.16)] dark:bg-[#25D366]" : l.no_ar ? "border-[1.5px] border-[#128C7E] dark:border-[#25D366]" : "bg-[#E88A8A]"}`}
                />
                <span className={`min-w-0 truncate ${l.usada ? "font-bold" : ""} ${pula ? "line-through decoration-[#A6907B]" : ""}`} title={l.nome}>{l.nome}</span>
                <span className="ml-auto shrink-0 whitespace-nowrap text-[10px] text-muted-foreground">
                  {PAPEL[l.papel]}{!l.no_ar ? " · fora" : ""}
                </span>
              </div>
            );
          })}
          {sai_por && !usadaNoCadastro && (
            <div className="flex min-w-0 items-center gap-2 text-[11.5px]">
              <span aria-hidden className="h-2 w-2 flex-none rounded-full bg-[#128C7E] shadow-[0_0_0_3px_rgba(7,94,84,0.16)] dark:bg-[#25D366]" />
              <span className="min-w-0 truncate font-bold" title={sai_por.nome}>{sai_por.nome}</span>
              <span className="ml-auto shrink-0 whitespace-nowrap text-[10px] text-muted-foreground">
                {sai_por.motivo === "oficial" ? "oficial · todas as suas linhas fora" : "onde o cliente está falando"}
              </span>
            </div>
          )}
        </div>
      </div>

      {ultimo && !enviando && <UltimoEnvio ultimo={ultimo} />}

      {bloqueio && !enviando && (
        <div className="relative rounded-lg border border-border bg-card py-2 pl-4 pr-2.5 text-[11.5px] leading-snug">
          <span aria-hidden className="absolute inset-y-0 left-0 w-1.5 rounded-l-lg bg-[repeating-linear-gradient(135deg,#5B0A0D_0_3px,#B4808C_3px_6px)] dark:bg-[repeating-linear-gradient(135deg,#C24545_0_3px,#5E2229_3px_6px)]" />
          {bloqueio.mensagem}
        </div>
      )}
    </div>
  );
}

function UltimoEnvio({ ultimo }: { ultimo: NonNullable<StatusEnvioProposta["ultimo"]> }) {
  const versao = ultimo.igual_atual ? "" : " Os ajustes feitos depois seguem no pedido, sem reenviar.";
  const pela = ultimo.linha ? ` pela ${ultimo.linha}` : "";
  if (ultimo.situacao === "lida" || ultimo.situacao === "entregue") {
    return (
      <div className="flex items-start gap-2 rounded-lg bg-[rgba(7,94,84,0.10)] px-2.5 py-2 text-[11.5px] leading-snug dark:bg-[rgba(37,211,102,0.12)]">
        <CheckCheck className="mt-0.5 h-3.5 w-3.5 flex-none text-[#075E54] dark:text-[#25D366]" />
        <span>
          <strong className="text-[#075E54] dark:text-[#25D366]">{ultimo.situacao === "lida" ? "Lida" : "Entregue"}.</strong>{" "}
          Saiu às {hora(ultimo.enviada_em)}{pela}.{versao}
          {ultimo.cliente_respondeu ? " O cliente já respondeu." : ""}
        </span>
      </div>
    );
  }
  if (ultimo.situacao === "nao_chegou") {
    return (
      <div className="relative rounded-lg border border-border bg-card py-2 pl-4 pr-2.5 text-[11.5px] leading-snug">
        <span aria-hidden className="absolute inset-y-0 left-0 w-1.5 rounded-l-lg bg-[repeating-linear-gradient(135deg,#5B0A0D_0_3px,#B4808C_3px_6px)] dark:bg-[repeating-linear-gradient(135deg,#C24545_0_3px,#5E2229_3px_6px)]" />
        <strong>Não chegou.</strong> Saiu às {hora(ultimo.enviada_em)}{pela}. {ultimo.erro}{versao}
      </div>
    );
  }
  return (
    <div className="flex items-start gap-2 rounded-lg bg-muted px-2.5 py-2 text-[11.5px] leading-snug">
      {ultimo.situacao === "saindo"
        ? <Loader2 className="mt-0.5 h-3.5 w-3.5 flex-none animate-spin text-muted-foreground motion-reduce:animate-none" />
        : ultimo.situacao === "enviada"
          ? <Check className="mt-0.5 h-3.5 w-3.5 flex-none text-muted-foreground" />
          : <Clock className="mt-0.5 h-3.5 w-3.5 flex-none text-muted-foreground" />}
      <span>
        {ultimo.situacao === "saindo"
          ? <>Saindo agora às {hora(ultimo.enviada_em)}{pela}. Esperando o WhatsApp confirmar o envio; a tela atualiza sozinha.</>
          : <>Enviada às {hora(ultimo.enviada_em)}{pela}.{versao}</>}
      </span>
    </div>
  );
}
